/**
 * SceneRenderer
 * 
 * Renders scene background and positioned elements using percentage-based positioning.
 * Handles element animations using AnimationController.
 */

import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import gsap from 'gsap';
import { Scene, SceneElement, CharacterPortrait } from '../types';
import { useGame } from '../systems';
import { AnimationController } from '../systems';
import {
  characterElementId,
  mergeSceneElementsForRender,
  parseCharacterIdFromTarget,
  resolveCharacterPortrait,
  resolveMoodImage,
} from '../systems/character-runtime';
import {
  applyBackgroundVideoAudio,
  holdBackgroundVideoLastFrame,
  isBackgroundVideoLoop,
  isVideoAsset,
} from '../systems/media-utils';
import { Mailbox, MailItem } from './mailbox';
import {
  getInspectedFlag,
  getReportedFlag,
  isInspectObjectElement,
  isObjectInspectScene,
  resolveInspectMarkerKind,
} from '../systems/object-inspect-utils';
import { SHIELD_ICON_SRC } from './shield-hud';
import { useAccessibility } from '../systems/accessibility-context';
import styles from './scene-renderer.module.css';

export interface SceneRendererHandle {
  changeMood: (elementId: string, mood: string, skipAnimation?: boolean) => Promise<void>;
  playEntranceAnimations: () => Promise<void>;
  getElement: (elementId: string) => HTMLDivElement | null;
  changeBackground: (backgroundUrl: string) => void;
}

interface SceneRendererProps {
  scene: Scene;
  characters?: CharacterPortrait[];
  onElementClick: (elementId: string) => void;
  onMailboxAction?: (elementId: string, actionType: 'reply' | 'archive' | 'report' | 'clickOnLink') => void;
  inventory?: string[];
  activeCharacterId?: string | null;
  isDialogueActive?: boolean;
  inspectModalOpen?: boolean;
}

export const SceneRenderer = forwardRef<SceneRendererHandle, SceneRendererProps>(
  ({ scene, characters, onElementClick, onMailboxAction, inventory = [], activeCharacterId, isDialogueActive = false, inspectModalOpen = false }, ref) => {
    const game = useGame();
    const { strictMode: a11yStrict } = useAccessibility();
    const animationControllerRef = useRef<AnimationController | null>(null);
    const elementRefsRef = useRef<Map<string, HTMLDivElement>>(new Map());
    const moodImageRefsRef = useRef<Map<string, { current: HTMLImageElement | null; next: HTMLImageElement | null }>>(new Map());
    const lastAnimatedSceneRef = useRef<string | null>(null);
    const [elementMoods, setElementMoods] = useState<Record<string, string>>({});
    const [currentBackground, setCurrentBackground] = useState<string | undefined>(scene.background);
    const [currentBackgroundLoop, setCurrentBackgroundLoop] = useState(
      isBackgroundVideoLoop(scene.backgroundLoop)
    );
    const backgroundVideoRef = useRef<HTMLVideoElement>(null);
    const lastLoadedBackgroundRef = useRef<string | undefined>(undefined);
    const [inspectHover, setInspectHover] = useState<{ elementId: string; x: number; y: number } | null>(null);
    const objectInspectMode = isObjectInspectScene(scene) && scene.type !== 'observation';
    // Champs hérités du format v1 (mode feedback), absents du type Scene v2.
    const legacyScene = scene as typeof scene & {
      feedbackSourceSceneId?: string;
      feedbackHighlight?: string;
      feedbackHighlightInEmail?: string[];
    };

    // Initialize animation controller
    useEffect(() => {
      animationControllerRef.current = new AnimationController();

      return () => {
        // Cleanup animations on unmount
        animationControllerRef.current?.killAll();
      };
    }, []);

    // Initialize moods when scene changes
    useEffect(() => {
      const initialMoods: Record<string, string> = {};
      const moodElements = characters?.length
        ? mergeSceneElementsForRender(scene, characters, game.scenario ?? undefined)
        : scene.elements ?? [];

      moodElements.forEach((element) => {
        if (element.type === 'character' && element.moods) {
          initialMoods[element.id] = element.defaultMood || 'default';
        }
      });
      setElementMoods(initialMoods);
      
      // Reset last animated scene when scene changes
      // This allows playEntranceAnimations to be called again if needed
      lastAnimatedSceneRef.current = null;
    }, [scene.id, scene.elements, characters]);

    useEffect(() => {
      setCurrentBackground(scene.background);
      setCurrentBackgroundLoop(isBackgroundVideoLoop(scene.backgroundLoop));
      setInspectHover(null);
    }, [scene.background, scene.backgroundLoop, scene.id]);

    const feedbackSourceScene = legacyScene.feedbackSourceSceneId && game.scenario
      ? game.scenario.scenes.find(s => s.id === legacyScene.feedbackSourceSceneId)
      : null;
    const displayBackground = feedbackSourceScene?.background ?? currentBackground;
    const displayBackgroundLoop = feedbackSourceScene
      ? isBackgroundVideoLoop(feedbackSourceScene.backgroundLoop)
      : currentBackgroundLoop;

    useEffect(() => {
      if (!displayBackground || !isVideoAsset(displayBackground)) {
        lastLoadedBackgroundRef.current = undefined;
        return;
      }
      const video = backgroundVideoRef.current;
      if (!video) return;
      if (lastLoadedBackgroundRef.current === displayBackground) return;
      lastLoadedBackgroundRef.current = displayBackground;
      video.load();
      // Mode accessibilité renforcée : vidéo de fond figée (applyBackgroundVideoAudio la lance).
      if (a11yStrict) video.muted = true;
      else applyBackgroundVideoAudio(video);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- relancé seulement au changement de fond
    }, [displayBackground]);

    // Mode accessibilité renforcée : vidéo de fond figée (image fixe).
    useEffect(() => {
      const video = backgroundVideoRef.current;
      if (!video) return;
      if (a11yStrict) video.pause();
      else void video.play().catch(() => {});
    }, [a11yStrict]);

    useImperativeHandle(ref, () => ({
      playEntranceAnimations: async () => {
        if (!animationControllerRef.current || !scene.elements) {
          return;
        }

        // Only animate if this is a new scene or hasn't been animated yet
        if (lastAnimatedSceneRef.current === scene.id) {
          return;
        }

        const controller = animationControllerRef.current;
        lastAnimatedSceneRef.current = scene.id;

        const animationPromises: Promise<any>[] = [];

        scene.elements.forEach((element) => {
          if (element.animation) {
            const elementRef = elementRefsRef.current.get(element.id);
            if (elementRef) {
              const tween = controller.animateElement(elementRef, element.animation);
              // Wrap the tween in a promise
              animationPromises.push(new Promise(resolve => {
                if (tween.isActive()) {
                  tween.eventCallback('onComplete', resolve);
                } else {
                  resolve(null);
                }
              }));
            }
          }
        });

        if (animationPromises.length > 0) {
          await Promise.all(animationPromises);
        }
      },
      changeMood: async (elementId: string, mood: string, skipAnimation = false): Promise<void> => {
        const renderElements = characters?.length
          ? mergeSceneElementsForRender(scene, characters, game.scenario ?? undefined)
          : scene.elements ?? [];
        let element = renderElements.find((e) => e.id === elementId);
        let nextImageUrl: string | undefined;

        if (element?.type === 'character' && element.moods) {
          nextImageUrl = element.moods[mood] || element.moods[element.defaultMood || 'default'];
        } else if (characters?.length) {
          const portrait = resolveCharacterPortrait(characters, elementId);
          if (portrait) {
            nextImageUrl = resolveMoodImage(portrait, mood);
            if (!element) {
              element = renderElements.find(
                (e) => e.id === characterElementId(portrait.id)
              );
            }
          }
        }

        if (!nextImageUrl) {
          console.warn(`Mood change requested for element ${elementId}, but no moods defined.`);
          return Promise.resolve();
        }

        setElementMoods((prev) => ({ ...prev, [elementId]: mood }));

        const moodRefs = moodImageRefsRef.current.get(elementId);
        if (!moodRefs || !moodRefs.current || !moodRefs.next || !animationControllerRef.current) {
          return Promise.resolve();
        }

        if (skipAnimation) {
          moodRefs.current.src = nextImageUrl;
          moodRefs.current.style.opacity = '1';
          moodRefs.next.style.opacity = '0';
          return Promise.resolve();
        }

        const currentFile = moodRefs.current.src.split('/').pop() ?? '';
        const nextFile = nextImageUrl.split('/').pop() ?? '';
        if (currentFile === nextFile) {
          moodRefs.current.style.opacity = '1';
          moodRefs.next.style.opacity = '0';
          return Promise.resolve();
        }

        moodRefs.next.src = nextImageUrl;

        await new Promise<void>((resolve) => {
          if (moodRefs.next!.complete) resolve();
          else {
            moodRefs.next!.onload = () => resolve();
            moodRefs.next!.onerror = () => resolve();
          }
        });

        await animationControllerRef.current.crossfadeMood(moodRefs.current, moodRefs.next, {
          duration: 0.6,
          ease: 'power1.inOut'
        });

        moodRefs.current.src = nextImageUrl;
        moodRefs.current.style.opacity = '1';
        moodRefs.current.style.display = 'block';
        moodRefs.next.style.opacity = '0';
        moodRefs.next.style.display = 'block';
      },
      getElement: (elementId: string) => {
        return elementRefsRef.current.get(elementId) || null;
      },
      changeBackground: (backgroundUrl: string) => {
        setCurrentBackground(backgroundUrl);
        setCurrentBackgroundLoop(true);
      },
    }));

    const handleElementClick = (elementId: string) => {
      const element = scene.elements?.find((item) => item.id === elementId);
      const elementRef = elementRefsRef.current.get(elementId);
      if (element?.type === 'object' && elementRef && !objectInspectMode) {
        const image = elementRef.querySelector('img');
        if (image) {
          gsap.fromTo(
            image,
            { scale: 1 },
            { scale: 1.25, duration: 0.2, ease: 'power2.out', yoyo: true, repeat: 1 }
          );
        }
      }
      onElementClick(elementId);
    };

    const isElementInspected = (elementId: string): boolean =>
      Boolean(game.gameState?.flags?.[getInspectedFlag(elementId)]);

    const isElementReported = (elementId: string): boolean =>
      Boolean(game.gameState?.flags?.[getReportedFlag(elementId)]);

    const renderInspectObject = (element: SceneElement) => {
      const inspected = isElementInspected(element.id);
      const reported = isElementReported(element.id);
      const markerKind = resolveInspectMarkerKind(element, inspected, reported);

      if (markerKind === 'shield') {
        return (
          <span className={styles.inspectMarker} aria-hidden="true">
            <img src={SHIELD_ICON_SRC} alt="" className={styles.inspectMarkerShield} />
          </span>
        );
      }
      if (markerKind === 'success') {
        return (
          <span className={`${styles.inspectMarker} ${styles.inspectMarkerSuccess}`} aria-hidden="true">
            ✓
          </span>
        );
      }
      if (markerKind === 'error') {
        return (
          <span className={`${styles.inspectMarker} ${styles.inspectMarkerError}`} aria-hidden="true">
            ✕
          </span>
        );
      }
      if (markerKind === 'warning') {
        return (
          <span className={styles.inspectMarker} aria-hidden="true">⚠️</span>
        );
      }

      return <div className={styles.inspectHotspot} aria-hidden="true" />;
    };

    const setElementRef = (id: string, ref: HTMLDivElement | null) => {
      if (ref) {
        elementRefsRef.current.set(id, ref);
      } else {
        elementRefsRef.current.delete(id);
      }
    };

    const setMoodImageRef = (id: string, type: 'current' | 'next', ref: HTMLImageElement | null) => {
      let refs = moodImageRefsRef.current.get(id);
      if (!refs) {
        refs = { current: null, next: null };
        moodImageRefsRef.current.set(id, refs);
      }
      refs[type] = ref;
    };

    const sourceScene = legacyScene.feedbackSourceSceneId && game.scenario
        ? game.scenario.scenes.find(s => s.id === legacyScene.feedbackSourceSceneId)
        : null;
      const baseElements = sourceScene?.elements ?? scene.elements ?? [];
      const sourceForCharacters = sourceScene ?? scene;
      const effectiveElements = characters?.length
        ? mergeSceneElementsForRender(
            { ...sourceForCharacters, elements: baseElements },
            characters,
            game.scenario ?? undefined
          )
        : baseElements;
      const isFeedbackMode = Boolean(legacyScene.feedbackSourceSceneId);
      const mailboxHighlight = isFeedbackMode && legacyScene.feedbackHighlight === 'email';

      const renderElementImage = (element: SceneElement, opts?: { readOnly?: boolean; highlight?: boolean; highlightText?: string[] }) => {
      // Handle mailbox elements
      if (element.type === 'mailbox' && element.mailbox) {
        const hasLinkAction = !opts?.readOnly && element.mailboxActions?.clickOnLink !== undefined;
        
        // Find other emails in the same chapter (only when not read-only)
        let emails: MailItem[] = [];
        if (!opts?.readOnly && game.scenario && game.scenario.metadata.chapters) {
          const scenario = game.scenario;
          const chapters = game.scenario.metadata.chapters;

          // Get chapter start indices
          const chapterStarts = chapters.map(c => ({
            id: c.id,
            index: scenario.scenes.findIndex(s => s.id === c.startScene)
          })).sort((a, b) => a.index - b.index);
          
          // Find current chapter
          const currentSceneIndex = scenario.scenes.findIndex(s => s.id === scene.id);
          let currentChapterId = chapterStarts[0]?.id;
          for (const start of chapterStarts) {
            if (currentSceneIndex >= start.index) {
              currentChapterId = start.id;
            } else {
              break;
            }
          }
          
          // Find all mailbox scenes in the same chapter
          const emailScenes = scenario.scenes.filter(s => {
            if (!s.elements?.some(e => e.type === 'mailbox')) return false;
            
            const sIndex = scenario.scenes.findIndex(sc => sc.id === s.id);
            let sChapterId = chapterStarts[0]?.id;
            for (const start of chapterStarts) {
              if (sIndex >= start.index) {
                sChapterId = start.id;
              } else {
                break;
              }
            }
            return sChapterId === currentChapterId;
          });
          
          // Map to MailItem
          emails = emailScenes.map(s => {
            const mElement = s.elements!.find(e => e.type === 'mailbox')!;
            return {
              id: s.id,
              subject: mElement.mailbox!.subject,
              isCurrent: s.id === scene.id
            };
          });
        }

        return (
          <Mailbox
            from={element.mailbox.from}
            to={element.mailbox.to}
            subject={element.mailbox.subject}
            content={element.mailbox.content}
            metadata={element.mailbox.metadata}
            onReply={() => onMailboxAction?.(element.id, 'reply')}
            onArchive={() => onMailboxAction?.(element.id, 'archive')}
            onReport={() => onMailboxAction?.(element.id, 'report')}
            onLinkClick={hasLinkAction ? () => onMailboxAction?.(element.id, 'clickOnLink') : undefined}
            emails={emails}
            readOnly={opts?.readOnly}
            highlight={opts?.highlight}
            highlightText={opts?.highlightText}
          />
        );
      }
      
      // Handle text elements
      if (element.type === 'text' && element.text) {
        const interpolatedText = game.interpolateText(element.text);
        
        return (
          <div 
            className={styles.textElement}
            style={{
              fontSize: element.textStyle?.fontSize || '1rem',
              fontFamily: element.textStyle?.fontFamily || 'Arial, sans-serif',
              fontWeight: element.textStyle?.fontWeight || 'normal',
              color: element.textStyle?.color || '#ffffff',
              textAlign: element.textStyle?.textAlign || 'center',
              backgroundColor: element.textStyle?.backgroundColor || 'transparent',
              padding: element.textStyle?.padding || '0',
              textShadow: element.textStyle?.textShadow || '2px 2px 4px rgba(0,0,0,0.8)',
            }}
          >
            <span style={{ color: element.textStyle?.color || '#ffffff' }}>
              {interpolatedText}
            </span>
          </div>
        );
      }
      
      // Handle character moods
      if (element.type === 'character' && element.moods) {
        const currentMood = elementMoods[element.id] || element.defaultMood || 'default';
        const currentImageUrl = element.moods[currentMood] || element.image;

        return (
          <div className={styles.imageContainer} style={{ opacity: 1 }}>
            <img
              ref={(ref) => setMoodImageRef(element.id, 'current', ref)}
              src={currentImageUrl}
              alt={`${element.id}-${currentMood}`}
              className={styles.elementImage}
              style={{ opacity: 1, display: 'block' }}
            />
            <img
              ref={(ref) => setMoodImageRef(element.id, 'next', ref)}
              alt={`${element.id}-next`}
              className={styles.elementImage}
              style={{ opacity: 0, display: 'block' }}
            />
          </div>
        );
      }

      return (
        element.image && (
          <img src={element.image} alt={element.id} className={styles.elementImage} />
        )
      );
    };

    const renderElementContent = (element: SceneElement, opts?: { readOnly?: boolean; highlight?: boolean; highlightText?: string[] }) => {
      if (objectInspectMode && isInspectObjectElement(element)) {
        return renderInspectObject(element);
      }
      return renderElementImage(element, opts);
    };

    return (
      <div className={styles.sceneRenderer}>
        {/* Background (keep email screen background when in feedback mode) */}
        {displayBackground && isVideoAsset(displayBackground) ? (
          <video
            ref={backgroundVideoRef}
            className={styles.backgroundVideo}
            src={displayBackground}
            autoPlay={!a11yStrict}
            loop={displayBackgroundLoop && !a11yStrict}
            preload="auto"
            muted
            playsInline
            onEnded={() => {
              if (displayBackgroundLoop) return;
              const video = backgroundVideoRef.current;
              if (video) holdBackgroundVideoLastFrame(video);
            }}
          />
        ) : displayBackground ? (
          <div
            className={styles.background}
            style={{
              backgroundImage: `url(${displayBackground})`,
            }}
          />
        ) : null}

        {effectiveElements?.filter(element => {
          const isHidden = game.gameState?.hiddenElements?.has(element.id);
          const isCollected = inventory.includes(element.id);
          if (element.condition && !game.evaluateCondition(element.condition)) return false;
          if (scene.type === 'observation' && isInspectObjectElement(element)) return false;
          return !isHidden && !isCollected;
        }).map((element) => {
          // Rule: "On ne doit voir que le personnage qui parle"
          // If a dialogue is active, hide all characters except the speaker
          const isCharacter = element.type === 'character';
          
          // Improved speaker detection: check if activeCharacterId matches element.id
          // activeCharacterId can be either elementId (from characterMood) or characterId (from portrait data)
          // Use exact match first, then check if element.id contains activeCharacterId or vice versa
          const isSpeaker = isCharacter && activeCharacterId && (
            element.id === activeCharacterId ||
            parseCharacterIdFromTarget(element.id) === activeCharacterId ||
            parseCharacterIdFromTarget(activeCharacterId) === parseCharacterIdFromTarget(element.id) ||
            element.id.includes(activeCharacterId) ||
            activeCharacterId.includes(element.id)
          );
          
          // Rule: "Quand le player parle, on n'enlève pas le character précédent, on le laisse."
          // We only hide a character if a DIFFERENT character is currently identified as the speaker.
          // Only hide if dialogue is active AND we have an active character AND it's not the speaker
          // IMPORTANT: If hideElement/showElement commands are used in the scene, they take precedence
          // and we should not apply automatic hiding to avoid flickering conflicts
          const shouldHideCharacter = isCharacter && isDialogueActive && activeCharacterId && !isSpeaker;

          // During dialogue, keep the speaker at their scene-defined horizontal position (e.g. left/right).
          let elementLeft = `${element.position.x}%`;
          if (isCharacter && isDialogueActive) {
            if (shouldHideCharacter) {
              elementLeft = '-20%'; // Hide others
            }
          }

          const isInspectObject = objectInspectMode && isInspectObjectElement(element);
          const inspected = isInspectObject && isElementInspected(element.id);
          const isInspectable = isInspectObject && !inspected && !inspectModalOpen;
          const elementClickable = Boolean(element.clickAction)
            && !element.noCursorChange
            && !isInspectObject;
          const inspectClickable = isInspectable && !isFeedbackMode && !shouldHideCharacter;

          return (
            <div
              key={element.id}
              ref={(ref) => setElementRef(element.id, ref)}
              className={`${styles.sceneElement} ${elementClickable ? styles.clickable : ''} ${isCharacter ? styles.characterElement : ''} ${isCharacter ? (shouldHideCharacter ? styles.characterHidden : styles.characterVisible) : ''} ${isInspectable ? styles.inspectHotspot : ''} ${inspected ? styles.inspectHotspotDisabled : ''}`}
              style={{
                left: elementLeft,
                top: isCharacter ? 'auto' : `${element.position.y}%`,
                bottom: isCharacter ? '0' : undefined,
                width: element.position.width ? `${element.position.width}%` : 'auto',
                height: isCharacter ? '75%' : (element.position.height ? `${element.position.height}%` : 'auto'),
                transform: isCharacter ? 'translateX(-50%)' : undefined,
                zIndex: isSpeaker ? 100 : (isCharacter ? 10 : 5),
                pointerEvents: shouldHideCharacter || inspected ? 'none' : (isFeedbackMode ? 'none' : 'auto'),
                cursor: isInspectable ? 'zoom-in' : undefined,
              }}
              onClick={() => {
                if (isFeedbackMode || shouldHideCharacter) return;
                if (inspectClickable) {
                  handleElementClick(element.id);
                  return;
                }
                if (element.clickAction && !isInspectObject) {
                  handleElementClick(element.id);
                }
              }}
              onMouseMove={isInspectable ? (event) => {
                setInspectHover({ elementId: element.id, x: event.clientX, y: event.clientY });
              } : undefined}
              onMouseLeave={isInspectable ? () => setInspectHover(null) : undefined}
            >
              {renderElementContent(element, element.type === 'mailbox' ? { readOnly: isFeedbackMode, highlight: element.type === 'mailbox' && mailboxHighlight, highlightText: legacyScene.feedbackHighlightInEmail } : undefined)}
            </div>
          );
        })}
        {inspectHover && (
          <div
            className={styles.inspectTooltip}
            style={{ left: inspectHover.x, top: inspectHover.y }}
          >
            Examiner
          </div>
        )}
      </div>
    );
  }
);

